import translate from '@/utils/translate';
import type {ContentSubmission} from '@/types/contentSubmission';
import type {BreadcrumbItem} from '@/components/breadcrumbs/Breadcrumbs';

export function buildCampaignNameBreadcrumbItem(
  label: string,
  campaignNetwork: string | null | undefined,
  link?: string
): BreadcrumbItem {
  return {
    label,
    ...(link ? {link} : {}),
    ...(campaignNetwork !== undefined ? {campaignNetwork} : {}),
  };
}

const CONTENT_KIND_KEYS: Record<string, string> = {
  photo: 'Photo',
  video: 'Video',
  blog: 'Blog',
  mixed_media: 'Story',
};

/**
 * Builds a breadcrumb label for a content submission, e.g. "Photo V3".
 *
 * When `versionId` is provided, the version number is derived from the
 * position of that version in the `versions` array. Otherwise falls back
 * to `versionCount` (the total / latest version number).
 */
const EXCLUDED_PARAMS = ['versionId', 'activeTabId'];

export function filterPreservedSearchParams(search: string): string {
  const params = new URLSearchParams(search);
  EXCLUDED_PARAMS.forEach((key) => params.delete(key));
  const result = params.toString();
  return result ? `?${result}` : '';
}

export function getContentSubmissionLabel(
  contentSubmission: ContentSubmission | undefined,
  versionId?: string | null
): string | undefined {
  if (!contentSubmission) return undefined;

  const kindKey = CONTENT_KIND_KEYS[contentSubmission.kind];
  const kindLabel = kindKey ? translate(kindKey) : contentSubmission.kind;

  let versionNumber: number | undefined;

  if (versionId && contentSubmission.versions?.length) {
    const idx = contentSubmission.versions.findIndex((v) => v.id === versionId);
    if (idx !== -1) {
      // versions array is ordered desc (newest first)
      versionNumber = contentSubmission.versions.length - idx;
    }
  }

  if (!versionNumber && contentSubmission.versionCount) {
    versionNumber = contentSubmission.versionCount;
  }

  return versionNumber && versionNumber > 1
    ? `${kindLabel} V${versionNumber}`
    : kindLabel;
}
